/**
 * v2 personal-data encryption (AES-256-GCM).
 *
 * The per-investigation `personal_data` KEK is unwrapped at login and cached in
 * localStorage as `kek_<investigationUuid>_personal_data` (raw 32-byte key, base64) —
 * see authService.handleLoginKeks. A personal-data record encrypted with v2 carries a
 * `nonce`; v1 (CryptoJS) records have none.
 */

import { encryptBlob, decryptBlob, base64ToUint8, uint8ToBase64 } from './encryption';

const PERSONAL_DATA_CATEGORY = 'personal_data';

function kekStorageKey(investigationUuid: string): string {
    return `kek_${investigationUuid}_${PERSONAL_DATA_CATEGORY}`;
}

/** Whether the v2 personal_data KEK is available for this investigation. */
export function hasPersonalDataKek(investigationUuid: string): boolean {
    return localStorage.getItem(kekStorageKey(investigationUuid)) !== null;
}

/**
 * Load the investigation's personal_data KEK as an AES-GCM CryptoKey.
 * Throws if the KEK is not cached — the researcher must have logged in via the
 * v2 flow for the KEK to be provisioned.
 */
export async function getPersonalDataKek(investigationUuid: string): Promise<CryptoKey> {
    const b64 = localStorage.getItem(kekStorageKey(investigationUuid));
    if (!b64) {
        throw new Error(`Missing personal_data KEK for investigation ${investigationUuid}`);
    }
    const rawKek = base64ToUint8(b64);
    return crypto.subtle.importKey('raw', rawKek, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** Encrypt one personal-data field value with AES-256-GCM. */
export async function encryptPersonalFieldV2(
    plaintext: string,
    kek: CryptoKey,
): Promise<{ value: string; nonce: string }> {
    const data = new TextEncoder().encode(plaintext);
    const { encrypted, nonce } = await encryptBlob(data.buffer, kek);
    return { value: uint8ToBase64(new Uint8Array(encrypted)), nonce };
}

/** Decrypt one personal-data field value written by encryptPersonalFieldV2. Throws if it cannot. */
export async function decryptPersonalFieldV2(ciphertext: string, nonce: string, kek: CryptoKey): Promise<string> {
    const plain = await decryptBlob(base64ToUint8(ciphertext).buffer as ArrayBuffer, nonce, kek);
    return new TextDecoder().decode(plain);
}
